import type { RouterContextType } from "@/api/permissions/permissions.api";
// import { CheckInOutInstructions } from "@/components/features/WorkOrders/NewWorkOrder/CheckInOutInstructions";
import { ClientLocationDetails } from "@/components/features/WorkOrders/NewWorkOrder/ClientLocationDetails";
import { ContactsCard } from "@/components/features/WorkOrders/NewWorkOrder/ContactsCard";
import { TradesSubtrades } from "@/components/features/WorkOrders/NewWorkOrder/TradesSubtrades";
import { UploadPhotos } from "@/components/features/WorkOrders/NewWorkOrder/UploadPhotos";
import { WorkOrderDetails } from "@/components/features/WorkOrders/NewWorkOrder/WorkOrderDetails";
import { Button } from "@/components/ui/Button/Button";
import Loader from "@/components/ui/Loader/Loader";
import { useClientOverviewDetails } from "@/hooks/clients/useClientListQueries";
import { useGetWorkOrderById } from "@/hooks/work-order/UseCreateWorkOrder";
import {
  transformWorkOrderToFormValues,
  useWorkOrderForm,
  WORK_ORDER_FIELD_LABELS,
  WORK_ORDER_FIELD_ORDER,
} from "@/hooks/work-order/useWorkOrderForm";
import styles from "@/routes/work-orders/add-work-order/add-work-order.module.scss";
import { authGuardWithPermissions } from "@/utils/authGuard";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import { showErrorAlert, handleApiSuccess } from "@/utils/responseHandler";
import { scrollToFirstError } from "@/utils/scrollToFirstError";
import { Box, Flex, Stack, Text, Title } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import {
  createFileRoute,
  retainSearchParams,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import type { FieldErrors } from "react-hook-form";
import { FormProvider } from "react-hook-form";

export const Route = createFileRoute("/work-orders/$workOrderId/edit-work-order/")({
  component: EditWorkOrder,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function EditWorkOrder() {
  const { workOrderId } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();

  const { data, isLoading, isError } = useGetWorkOrderById(workOrderId);

  const clientIdForOverview = data?.data?.client ? null : (data?.data?.clientId ?? null);
  const { data: clientOverview } = useClientOverviewDetails(clientIdForOverview);

  const initialValues = useMemo(() => {
    if (!data?.data) return undefined;
    return transformWorkOrderToFormValues(data.data);
  }, [data]);

  const handleClose = () => {
    if (router.history.length > 1) {
      router.history.back();
    } else {
      navigate({ to: "/work-orders/$workOrderId", params: { workOrderId } });
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isError || !data?.data) {
    return (
      <Flex align="center" justify="center" h="70vh">
        <Text c="red">Failed to load work order. Please try again.</Text>
      </Flex>
    );
  }

  const initialClientLabel = data.data.client?.businessName ?? clientOverview?.businessName ?? "";
  const initialBrandLabel = data.data.brand.name;

  return (
    <EditWorkOrderForm
      workOrderId={workOrderId}
      initialValues={initialValues}
      initialClientLabel={initialClientLabel}
      initialBrandLabel={initialBrandLabel}
      woStatusId={data.data.woStatusId}
      onClose={handleClose}
    />
  );
}

interface EditWorkOrderFormProps {
  workOrderId: string;
  initialValues: ReturnType<typeof transformWorkOrderToFormValues> | undefined;
  initialClientLabel: string;
  initialBrandLabel: string;
  woStatusId?: number;
  onClose: () => void;
}

function EditWorkOrderForm({
  workOrderId,
  initialValues,
  initialClientLabel,
  initialBrandLabel,
  woStatusId,
  onClose,
}: EditWorkOrderFormProps) {
  const navigate = useNavigate();

  const { methods, onSubmit, isFormSubmitted, isSubmitting, submitError } = useWorkOrderForm({
    initialValues,
    mode: "edit",
    workOrderId,
    woStatusId,
    onEditSuccess: () => {
      navigate({ to: "/work-orders/$workOrderId", params: { workOrderId } });
    },
  });

  const onInvalid = (errors: FieldErrors) => {
    const errorFields = Object.keys(errors)
      .map((field) => WORK_ORDER_FIELD_LABELS[field] ?? field)
      .join(", ");
    showErrorAlert(`Please fill in the required fields: ${errorFields}`, "Validation Error");
    scrollToFirstError(errors, methods.setFocus, WORK_ORDER_FIELD_ORDER);
  };

  useEffect(() => {
    if (submitError) {
      showErrorAlert("Failed to update work order. Please try again.");
    }
  }, [submitError]);

  useEffect(() => {
    if (isFormSubmitted) {
      handleApiSuccess("Work order updated successfully!");
    }
  }, [isFormSubmitted]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit((data) => onSubmit(data), onInvalid)}>
        <Box component="section" className={styles.section}>
          <Flex justify="space-between" align="center" className={styles.header}>
            <Title order={3} className={styles.title}>
              Edit Work Order
            </Title>
            <IconX size={16} className={styles.closeIcon} onClick={onClose} aria-label="Close" />
          </Flex>
          <Box className={styles.container}>
            <Flex gap="xl" style={{ width: "100%" }}>
              <Stack gap="lg" style={{ flex: 1 }}>
                <ClientLocationDetails
                  isEditMode={true}
                  initialClientLabel={initialClientLabel}
                  initialBrandLabel={initialBrandLabel}
                />
                <WorkOrderDetails isEditMode={true} />
                <TradesSubtrades />
                {/* SERV-1926: Commenting out for MVP */}
                {/* <CheckInOutInstructions /> */}
                <UploadPhotos />
              </Stack>

              <Stack gap="lg" style={{ flex: 0.5 }}>
                <ContactsCard />
              </Stack>
            </Flex>
          </Box>
          <Box className={styles.footer}>
            <Box className={styles.footerContent}>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Update Work Order
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
}
